import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'

export const changeRange = (range, field, value) => {
  // Keyboard entry of a year emits partial dates such as 0002-01-05 before the full year.
  if (!value || value < '1000') {
    return range
  }
  const { startDate, endDate } = range || {}
  if (field === 'startDate') {
    return { startDate: value, endDate: !endDate || endDate < value ? value : endDate }
  }
  return { startDate: !startDate || startDate > value ? value : startDate, endDate: value }
}

const FIELDS = ['startDate', 'endDate']
const COMMIT_DELAY_MS = 600

const sameRange = (a, b) => a.startDate === b.startDate && a.endDate === b.endDate

const DateRangePicker = ({ range, max, clearable, onChange }) => {
  const { startDate = '', endDate = '' } = range || {}
  const [drafts, setDrafts] = useState({})
  const draftsRef = useRef({})
  const rangeRef = useRef(range)
  const onChangeRef = useRef(onChange)
  const timer = useRef(null)
  rangeRef.current = range
  onChangeRef.current = onChange

  const replaceDrafts = (next) => {
    draftsRef.current = next
    setDrafts(next)
  }
  const cancelTimer = () => clearTimeout(timer.current)
  useEffect(() => {
    draftsRef.current = {}
    setDrafts({})
  }, [startDate, endDate])
  useEffect(() => cancelTimer, [])

  const commit = (fields) => {
    cancelTimer()
    const pending = draftsRef.current
    const current = rangeRef.current
    const next = fields
      .filter((field) => pending[field] !== undefined)
      .reduce((acc, field) => changeRange(acc, field, pending[field]), current)
    const remaining = { ...pending }
    fields.forEach((field) => delete remaining[field])
    replaceDrafts(remaining)
    if (next !== current && !(current && sameRange(next, current))) {
      onChangeRef.current(next)
    }
  }

  const shown = (field, value) => (drafts[field] !== undefined ? drafts[field] : value)
  const update = (field) => (e) => {
    replaceDrafts({ ...draftsRef.current, [field]: e.target.value })
    cancelTimer()
    timer.current = setTimeout(() => commit(FIELDS), COMMIT_DELAY_MS)
  }
  const keyDown = (field) => (e) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      commit([field])
    }
  }
  return (
    <div className='d-flex align-items-center gap-2'>
      <label className='mb-0' htmlFor='tx-range-from'>
        From
      </label>
      <input
        id='tx-range-from'
        type='date'
        className='form-control'
        value={shown('startDate', startDate)}
        max={max}
        onChange={update('startDate')}
        onBlur={() => commit(['startDate'])}
        onKeyDown={keyDown('startDate')}
      />
      <label className='mb-0' htmlFor='tx-range-to'>
        To
      </label>
      <input
        id='tx-range-to'
        type='date'
        className='form-control'
        value={shown('endDate', endDate)}
        max={max}
        onChange={update('endDate')}
        onBlur={() => commit(['endDate'])}
        onKeyDown={keyDown('endDate')}
      />
      <Button
        className='btn btn-md btn-white'
        title='Clear dates'
        aria-label='Clear dates'
        disabled={!clearable}
        onClick={() => {
          cancelTimer()
          replaceDrafts({})
          onChange(null)
        }}
      >
        <i className='fa fa-close' aria-hidden='true' />
      </Button>
    </div>
  )
}

export default DateRangePicker
